import React from 'react';
import AppSplash from 'components/AppSplash';

const viewCache = {};
const subscribers = new Set();

const notifySubscribers = () => {
    subscribers.forEach(fn => fn());
};

const createCachedView = (key) => {
    class CachedView extends React.Component {
        componentDidMount() {
            this.unsubscribe = () => subscribers.delete(this.handleCacheUpdate);
            subscribers.add(this.handleCacheUpdate);
        }

        componentWillUnmount() {
            if (this.unsubscribe) {
                this.unsubscribe();
            }
        }

        handleCacheUpdate = () => {
            if (viewCache[key]) {
                this.forceUpdate();
            }
        };

        render() {
            const Component = viewCache[key];
            if (Component) {
                return <Component {...this.props} />;
            }
            return (
                <AppSplash className="loading">
                    <div id="connecting" className="current-state"></div>
                </AppSplash>
            );
        }
    }
    CachedView.viewName = key;
    return CachedView;
};

export const LANDING_VIEWS = [
    'StartScreen',
    'SignupFlowPage', 'SignupGoalPage', 'SignupEmailPage', 'QuickstartInit',
    'TrainerMpInit', 'MealPlanInit', 'MacroCalcInit', 'WorkoutRoutinesInit',
    'SignIn',
    'Main', 'TrainerMain',
    'OnboardingFlow',
    'ConversionPage',
    'BodyVisualizerApp',
    'BodyVisualizerFlow',
    'BuildRoutineFromScratchFlow'
];

const viewDefinitions = {
    // Landing pages - each file gets its own chunk
    StartScreen: [() => import(/* webpackChunkName: "v-start" */ 'views/StartScreen'), 'default'],
    SignupFlowPage: [() => import(/* webpackChunkName: "v-signup" */ 'views/SignUp'), 'default'],
    SignupGoalPage: [() => import(/* webpackChunkName: "v-signup" */ 'views/SignUp'), 'SignupGoalPage'],
    SignupEmailPage: [() => import(/* webpackChunkName: "v-signup" */ 'views/SignUp'), 'SignupEmailPage'],
    QuickstartInit: [() => import(/* webpackChunkName: "v-signup" */ 'views/SignUp'), 'QuickstartInit'],
    Quickstart: [() => import(/* webpackChunkName: "v-signup" */ 'views/SignUp'), 'Quickstart'],
    TrainerInvitePage: [() => import(/* webpackChunkName: "v-signup" */ 'views/SignUp'), 'TrainerInvitePage'],
    SignIn: [() => import(/* webpackChunkName: "v-signin" */ 'views/SignIn'), 'default'],
    Main: [() => import(/* webpackChunkName: "v-main" */ 'views/Main'), 'default'],
    TrainerMain: [() => import(/* webpackChunkName: "v-trainermain" */ 'views/TrainerMain'), 'default'],
    OnboardingFlow: [() => import(/* webpackChunkName: "v-onboard" */ 'views/OnboardingFlow'), 'default'],
    ConversionPage: [() => import(/* webpackChunkName: "v-conversion" */ 'views/Conversion'), 'default'],
    MealPlanInit: [() => import(/* webpackChunkName: "v-mpsetup" */ 'views/MealPlanSetupFlow'), 'MealPlanInit'],
    MacroCalcInit: [() => import(/* webpackChunkName: "v-mpsetup" */ 'views/MealPlanSetupFlow'), 'MacroCalcInit'],
    TrainerMpInit: [() => import(/* webpackChunkName: "v-trainersignup" */ 'views/TrainerSignup'), 'TrainerMpInit'],
    WorkoutRoutinesInit: [() => import(/* webpackChunkName: "v-routines" */ 'views/WorkoutRoutines'), 'WorkoutRoutinesInit'],
    UserWorkoutRoutines: [() => import(/* webpackChunkName: "v-routines" */ 'views/UserWorkoutRoutines'), 'default'],

    // Non-landing views from same files as landing pages
    MealPlanSetupFlow: [() => import(/* webpackChunkName: "v-mpsetup" */ 'views/MealPlanSetupFlow'), 'default'],
    MealPlanSignup: [() => import(/* webpackChunkName: "v-mpsetup" */ 'views/MealPlanSetupFlow'), 'MealPlanSignup'],
    GenerateNewWeek: [() => import(/* webpackChunkName: "v-mpsetup" */ 'views/MealPlanSetupFlow'), 'GenerateNewWeek'],
    RecipePickClientSwitcher: [() => import(/* webpackChunkName: "v-mpsetup" */ 'views/MealPlanSetupFlow'), 'RecipePickClientSwitcher'],
    TrainerSignup: [() => import(/* webpackChunkName: "v-trainersignup" */ 'views/TrainerSignup'), 'default'],

    // Workout views
    WorkoutPreviewPage: [() => import(/* webpackChunkName: "views" */ 'views/WorkoutPreview'), 'default'],
    WorkoutPreviewShortcut: [() => import(/* webpackChunkName: "views" */ 'views/WorkoutPreview'), 'WorkoutPreviewShortcut'],
    EditRoutinePage: [() => import(/* webpackChunkName: "views" */ 'views/EditRoutine'), 'default'],
    EditRoutineShortcut: [() => import(/* webpackChunkName: "views" */ 'views/EditRoutine'), 'EditRoutineShortcut'],
    EditRoutineStandalonePage: [() => import(/* webpackChunkName: "views" */ 'views/EditRoutine'), 'EditRoutineStandalonePage'],
    WorkoutLogPage: [() => import(/* webpackChunkName: "views" */ 'views/WorkoutLog'), 'default'],
    ProgressionPick: [() => import(/* webpackChunkName: "views" */ 'views/ProgressionPick'), 'default'],
    ProgressionShowPage: [() => import(/* webpackChunkName: "views" */ 'views/ProgressionPick'), 'ProgressionShowPage'],
    SwapExerciseSpec: [() => import(/* webpackChunkName: "views" */ 'views/SwapExerciseSpec'), 'default'],
    EditWorkoutTemplate: [() => import(/* webpackChunkName: "views" */ 'views/EditWorkoutTemplate'), 'default'],
    WorkoutDo: [() => import(/* webpackChunkName: "views" */ 'views/WorkoutDo'), 'default'],
    ProgressionTest: [() => import(/* webpackChunkName: "views" */ 'views/ProgressionTest'), 'default'],
    StrengthTest: [() => import(/* webpackChunkName: "views" */ 'views/StrengthTest'), 'default'],
    StrengthTestFormPage: [() => import(/* webpackChunkName: "views" */ 'views/StrengthTest'), 'StrengthTestFormPage'],
    SwapExerciseTemplate: [() => import(/* webpackChunkName: "views" */ 'views/SwapExerciseTemplate'), 'default'],
    AddExercises: [() => import(/* webpackChunkName: "views" */ 'views/AddExercises'), 'default'],
    PickLoggedExercise: [() => import(/* webpackChunkName: "views" */ 'views/PickLoggedExercise'), 'default'],
    WorkoutImageCats: [() => import(/* webpackChunkName: "views" */ 'views/WorkoutImageCats'), 'default'],
    WorkoutImageCat: [() => import(/* webpackChunkName: "views" */ 'views/WorkoutImageCat'), 'default'],
    AssignRoutine: [() => import(/* webpackChunkName: "views" */ 'views/WorkoutPlan'), 'AssignRoutine'],
    StandaloneWorkoutPlanPage: [() => import(/* webpackChunkName: "views" */ 'views/WorkoutPlan'), 'StandaloneWorkoutPlanPage'],

    // Progress/Stats views
    Progress: [() => import(/* webpackChunkName: "views" */ 'views/Progress'), 'default'],
    ClientProgressShortcut: [() => import(/* webpackChunkName: "views" */ 'views/Progress'), 'ClientProgressShortcut'],
    MacroAnalytics: [() => import(/* webpackChunkName: "views" */ 'views/MacroAnalytics'), 'default'],
    ClientMacroAnalyticsShortcut: [() => import(/* webpackChunkName: "views" */ 'views/MacroAnalytics'), 'ClientMacroAnalyticsShortcut'],
    BodyMeasurements: [() => import(/* webpackChunkName: "views" */ 'views/BodyMeasurements'), 'default'],
    ClientBmShortcut: [() => import(/* webpackChunkName: "views" */ 'views/BodyMeasurements'), 'ClientBmShortcut'],
    BodyVisualizerFlow: [() => import(/* webpackChunkName: "v-bodyvisualizer" */ 'views/BodyVisualizerFlow'), 'default'],
    BodyVisualizerApp: [() => import(/* webpackChunkName: "v-bodyvisualizer" */ 'views/BodyVisualizerApp'), 'default'],
    ProgressPhotos: [() => import(/* webpackChunkName: "views" */ 'views/ProgressPhotos'), 'default'],
    ClientProgressPicsShortcut: [() => import(/* webpackChunkName: "views" */ 'views/ProgressPhotos'), 'ClientProgressPicsShortcut'],

    // Meal views
    UserMealPage: [() => import(/* webpackChunkName: "views" */ 'views/ViewUserMeal'), 'default'],
    EditWeeklyMeal: [() => import(/* webpackChunkName: "views" */ 'views/EditWeeklyMeal'), 'default'],
    FindMealReplacement: [() => import(/* webpackChunkName: "views" */ 'views/FindMealReplacement'), 'default'],
    MealBrowserPage: [() => import(/* webpackChunkName: "views" */ 'views/RecipeBrowser'), 'MealBrowserPage'],
    RecipeBrowserPage: [() => import(/* webpackChunkName: "views" */ 'views/RecipeBrowser'), 'RecipeBrowserPage'],
    EditTempMeal: [() => import(/* webpackChunkName: "views" */ 'views/EditTempMeal'), 'default'],
    FindRecipeReplacement: [() => import(/* webpackChunkName: "views" */ 'views/FindRecipeReplacement'), 'default'],
    FindTempRecipeReplacement: [() => import(/* webpackChunkName: "views" */ 'views/FindTempRecipeReplacement'), 'default'],
    ViewBrowserRecipe: [() => import(/* webpackChunkName: "views" */ 'views/ViewMeal'), 'ViewBrowserRecipe'],
    ViewRecipeReplacement: [() => import(/* webpackChunkName: "views" */ 'views/ViewMeal'), 'ViewRecipeReplacement'],
    ViewTempMeal: [() => import(/* webpackChunkName: "views" */ 'views/ViewMeal'), 'ViewTempMeal'],
    ViewTempRecipeReplacement: [() => import(/* webpackChunkName: "views" */ 'views/ViewMeal'), 'ViewTempRecipeReplacement'],
    RecipeEditor: [() => import(/* webpackChunkName: "views" */ 'views/RecipeEditor'), 'default'],
    AddIngredientPage: [() => import(/* webpackChunkName: "views" */ 'views/RecipeEditor'), 'AddIngredientPage'],
    RegenerateMealPlanPage: [() => import(/* webpackChunkName: "views" */ 'views/RegenerateMealPlan'), 'default'],
    OffPlanMeals: [() => import(/* webpackChunkName: "views" */ 'views/OffPlanMeals'), 'default'],
    AddPeopleToMeals: [() => import(/* webpackChunkName: "views" */ 'views/AddPeopleToMeals'), 'default'],
    StandaloneMealPlanPage: [() => import(/* webpackChunkName: "views" */ 'views/MealPlan'), 'StandaloneMealPlanPage'],
    StandaloneGroceryListPage: [() => import(/* webpackChunkName: "views" */ 'views/GroceryList'), 'StandaloneGroceryListPage'],
    ClientMealPlans: [() => import(/* webpackChunkName: "views" */ 'views/ClientMealPlans'), 'default'],
    ViewSharedMeals: [() => import(/* webpackChunkName: "views" */ 'views/ViewSharedMeals'), 'default'],
    NutritionInfo: [() => import(/* webpackChunkName: "views" */ 'views/NutritionInfo'), 'default'],

    // Settings/Account views
    Settings: [() => import(/* webpackChunkName: "views" */ 'views/Settings'), 'default'],
    Account: [() => import(/* webpackChunkName: "views" */ 'views/Account'), 'default'],
    PastDuePage: [() => import(/* webpackChunkName: "views" */ 'views/Account'), 'PastDuePage'],
    Notifications: [() => import(/* webpackChunkName: "views" */ 'views/Notifications'), 'default'],
    ContactPage: [() => import(/* webpackChunkName: "views" */ 'views/Contact'), 'default'],
    HelpPage: [() => import(/* webpackChunkName: "views" */ 'views/Help'), 'default'],
    GetTheApp: [() => import(/* webpackChunkName: "views" */ 'views/GetTheApp'), 'default'],
    ShowErrorLog: [() => import(/* webpackChunkName: "views" */ 'views/ShowErrorLog'), 'default'],
    SwitchAccountLoader: [() => import(/* webpackChunkName: "views" */ 'views/SwitchAccountLoader'), 'default'],

    // Trainer views
    AssignClientRoutine: [() => import(/* webpackChunkName: "views" */ 'views/AssignClientRoutine'), 'default'],
    AssignRoutineToClients: [() => import(/* webpackChunkName: "views" */ 'views/AssignClientRoutine'), 'AssignRoutineToClients'],
    ClientBasicProfile: [() => import(/* webpackChunkName: "views" */ 'views/ClientBasicProfile'), 'default'],
    ClientDetails: [() => import(/* webpackChunkName: "views" */ 'views/ClientDetails'), 'default'],
    ClientMealPlanSettings: [() => import(/* webpackChunkName: "views" */ 'views/ClientMealPlanSettings'), 'default'],
    TrainerConversionPage: [() => import(/* webpackChunkName: "views" */ 'views/TrainerConversion'), 'default'],
    ViewLogs: [() => import(/* webpackChunkName: "views" */ 'views/ViewLogs'), 'default'],
    ViewLogsShortcut: [() => import(/* webpackChunkName: "views" */ 'views/ViewLogs'), 'ViewLogsShortcut'],
    Habits: [() => import(/* webpackChunkName: "views" */ 'views/Habits'), 'default'],
    AssignHabits: [() => import(/* webpackChunkName: "views" */ 'views/AssignHabits'), 'default'],
    AssignHabit: [() => import(/* webpackChunkName: "views" */ 'views/AssignHabits'), 'AssignHabit'],
    TrainerBrandingFlow: [() => import(/* webpackChunkName: "views" */ 'views/TrainerBrandingFlow'), 'default'],
    TrainerCancel: [() => import(/* webpackChunkName: "views" */ 'views/TrainerCancel'), 'default'],
    ChatPage: [() => import(/* webpackChunkName: "views" */ 'views/ChatPage'), 'default'],
    ClientMessaging: [() => import(/* webpackChunkName: "views" */ 'views/ClientMessaging'), 'default'],
    TrainerInvoices: [() => import(/* webpackChunkName: "views" */ 'views/TrainerInvoices'), 'default'],
    BrandedAppSetup: [() => import(/* webpackChunkName: "views" */ 'views/BrandedAppSetup'), 'default'],
    BrandedApps: [() => import(/* webpackChunkName: "views" */ 'views/BrandedApps'), 'default'],
    LogActivity: [() => import(/* webpackChunkName: "views" */ 'views/LogActivity'), 'default'],
    PDFSetupDone: [() => import(/* webpackChunkName: "views" */ 'views/PdfSetupDone'), 'default'],
    FormsIndex: [() => import(/* webpackChunkName: "views" */ 'views/FormsIndex'), 'default'],
    EditTrainerForm: [() => import(/* webpackChunkName: "views" */ 'views/EditTrainerForm'), 'default'],
    AddAssessmentsIndex: [() => import(/* webpackChunkName: "views" */ 'views/AddAssessmentsIndex'), 'default'],
    AddExercisesToForm: [() => import(/* webpackChunkName: "views" */ 'views/AddExercisesToForm'), 'default'],
    AssignForms: [() => import(/* webpackChunkName: "views" */ 'views/AssignForms'), 'default'],
    ClientViewAssignedForm: [() => import(/* webpackChunkName: "views" */ 'views/ViewAssignedForm'), 'ClientViewAssignedForm'],
    TrainerViewAssignedForm: [() => import(/* webpackChunkName: "views" */ 'views/ViewAssignedForm'), 'TrainerViewAssignedForm'],

    // Misc views
    WorkoutSetupFlowPage: [() => import(/* webpackChunkName: "views" */ 'views/WorkoutSetupFlow'), 'default'],
    BuildRoutineFromScratchFlow: [() => import(/* webpackChunkName: "v-workout-builder" */ 'views/BuildRoutineFromScratchFlow'), 'default'],
    SaleRedirector: [() => import(/* webpackChunkName: "views" */ 'views/SaleRedirector'), 'default'],
    AnatomicGifGenPage: [() => import(/* webpackChunkName: "views" */ 'views/AnatomicGifGen'), 'default'],
    WorkoutsOnboarding: [() => import(/* webpackChunkName: "views" */ 'views/WorkoutsOnboarding'), 'default'],
};

const lazyViews = {};
Object.keys(viewDefinitions).forEach(key => {
    lazyViews[key] = createCachedView(key);
});

export const preloadView = async (viewName) => {
    if (!viewDefinitions[viewName] || viewCache[viewName]) return;

    const [importFn, exportName] = viewDefinitions[viewName];
    const module = await importFn();

    viewCache[viewName] = exportName === 'default' ? module.default : module[exportName];

    // Also cache other views from the same module
    const importPath = importFn.toString();
    for (const [otherKey, [otherImportFn, otherExportName]] of Object.entries(viewDefinitions)) {
        if (otherKey !== viewName && otherImportFn.toString() === importPath) {
            viewCache[otherKey] = otherExportName === 'default' ? module.default : module[otherExportName];
        }
    }

    notifySubscribers();
};

export const preloadAllViews = async () => {
    const moduleCache = {};
    const loadPromises = [];

    for (const [viewKey, [importFn, exportName]] of Object.entries(viewDefinitions)) {
        if (viewCache[viewKey]) continue;

        const importPath = importFn.toString();
        if (!moduleCache[importPath]) {
            moduleCache[importPath] = importFn();
        }

        loadPromises.push(
            moduleCache[importPath].then(module => {
                viewCache[viewKey] = exportName === 'default' ? module.default : module[exportName];
            })
        );
    }

    await Promise.all(loadPromises);
    notifySubscribers();
};

export default lazyViews;
